/**
 * Service layer — Flights and the Flight Plan (2026-08-26).
 *
 * A milestone put into execution becomes a flight with a real flight number, crewed with team
 * members. All access goes through /api/flights (Clerk-authed, manager-and-up). The flight
 * tables are RLS deny-all, so there is deliberately no browser-side Supabase path.
 *
 * See docs/FLIGHT_PLAN.md for the operating model and database/193_flights.sql for the schema.
 */

// The Clerk token getter (window.__clerkGetToken) is installed by OutlineView's mount effect — and
// React runs a CHILD's effects before its parent's, so MyFlightsStrip's very first crew-index call
// used to go out before the getter existed, unauthenticated, get a 401, and cache "not crewed" for
// the whole session (Matt, 2026-09-03: "I am on two flights but it says I am uncrewed"). Wait for
// the getter and for a non-null token the same way services/_api.js does; send without one only
// after the wait is exhausted (signed-out is a real state and must still fail fast server-side).
async function authToken() {
  if (typeof window === 'undefined') return null
  for (let i = 0; i < 20; i++) {
    if (typeof window.__clerkGetToken === 'function') {
      try {
        const t = await window.__clerkGetToken()
        if (t) return t
      } catch { /* Clerk not ready yet — keep waiting */ }
    }
    await new Promise(r => setTimeout(r, 150))
  }
  return null
}

async function post(body) {
  const token = await authToken()
  const res = await fetch('/api/flights', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || ('Request failed: ' + res.status))
  return json
}

// Every write that changes who is crewed on what announces itself, so the outline's crew chips
// and My Work (frontend/src/outline/crew.js) refresh their index without the board having to
// know they exist. Fired AFTER the server confirmed the write — never optimistically.
const CREW_CHANGED = 'crew-index-changed'
async function postAndAnnounce(body) {
  const out = await post(body)
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(CREW_CHANGED))
  return out
}

/**
 * Every open flight with its live crew (and a squadron's member threads) — the cheap read the
 * whole app uses for "who is flying what" now that RASCI is gone. Any signed-in user may call it
 * (the server gates only the writes on the workload/admin seat). See api/flights.js#crewIndex
 * and frontend/src/outline/crew.js, which owns the cache and the by-milestone/by-thread maps.
 */
export function getCrewIndex() {
  return post({ action: 'crewIndex' })
}

/**
 * The Flow monitor (Flow Reconciliation Step 4): where time pools by hill stage, seats in use
 * over time, throughput, inventory, flow time, tower latency, the scheduler's deferrals, slip by
 * project, and full-kit waivers — all read from what the flight system already records. Tower
 * seats only (workload/admin). See api/flights.js#flowMonitor for the honesty rule on thin history.
 */
export function getFlowMonitor() {
  return post({ action: 'flowMonitor' })
}

/**
 * THE ROPE (sheet 2's launch check, database/229). Ask before opening the crew picker whether a
 * milestone may launch at all: the gate must be cleared and its leg must have room. Returns
 * { ok: true } or { refused: { kind, title, message } }. The real launch (createFlight) runs the
 * same check again server-side, with the crew, and logs a refusal; this one only asks.
 */
export function checkLaunch(milestoneId) {
  return post({ action: 'launchCheck', milestoneId })
}

/** The legs of the flight: who is in each, who is waiting behind it, what each holds. */
export function getLegs() {
  return post({ action: 'legs' })
}

/** Change how many planes a leg holds at once (null = no limit). Visible to everyone; that is the point. */
export function setLegHolds(key, holds) {
  return post({ action: 'setLegHolds', key, holds })
}

/**
 * The whole board in one call: flights in the air, the queue, holding, and the totals.
 *
 * `stats.postedSource` says where "is it posted" came from. 'airtable' is authoritative;
 * 'mirror-stale' means Airtable could not be reached and the numbers are running on the
 * scheduling mirror, which lags badly and will show finished work as overdue. Surface that
 * to the user rather than presenting a stale board as fact.
 *
 * `projectDirectory` (id → name) tops up milestones whose project the mirror never named.
 */
export async function getFlightPlan(projectDirectory) {
  const plan = await post({ action: 'flightPlan' })
  if (projectDirectory) {
    const fill = (m) => {
      if (!m.projectName && m.projectId && projectDirectory[m.projectId]) m.projectName = projectDirectory[m.projectId]
      return m
    }
    const fillProject = (p) => {
      if (!p.projectName && p.projectId && projectDirectory[p.projectId]) p.projectName = projectDirectory[p.projectId]
      p.milestones = (p.milestones || []).map(fill)
      if (p.next) fill(p.next)
      return p
    }
    plan.projects = (plan.projects || []).map(fillProject)
    plan.grounded = (plan.grounded || []).map(fillProject)
    plan.holding = (plan.holding || []).map(fill)
    plan.flights = (plan.flights || []).map(fill)
    plan.stats.unresolvedProject = (plan.projects || []).filter(p => !p.projectName).length
  }
  return plan
}

/**
 * Put a milestone in the air. Mints its flight number from the Deliverable Type's curated
 * abbreviation (or derived initials) plus the next number in that prefix's own sequence.
 * `members`: [{ memberAirtableId, memberName, isCaptain }]
 */
export function createFlight(milestoneId, members, hill, waiverNote) {
  // The rope runs first: a refused launch returns `{ refused }` (gate open, leg full, no crew slot)
  // and nothing is minted. Full kit (Step 2) runs after it: with an incomplete kit and no
  // `waiverNote` the server does NOT create the flight — it returns `{ kitRequired: true, kit }`
  // and the caller asks the tower for a reason.
  return postAndAnnounce({ action: 'createFlight', milestoneId, members, hill, waiverNote: waiverNote || undefined })
}

/** "I have no plane today": a Slack DM to whoever holds the Operations Director role (the morning crew card, no-plane case). */
export function askTower(memberId, memberName) {
  return post({ action: 'askTower', memberId, memberName })
}

/** Re-crew a flight. Anyone taken off is retired with a date, never deleted. */
export function setFlightCrew(flightId, members) {
  return postAndAnnounce({ action: 'setCrew', flightId, members })
}

/** Move a flight between 'boarding' and 'in_flight'. Departure is stamped on the first takeoff. */
export function setFlightStatus(flightId, status) {
  return post({ action: 'setStatus', flightId, status })
}

/**
 * Move a flight along the hill. `position` is 0..1 (uphill → peak → downhill).
 *
 * The note is not decoration: a hill chart without it is a prettier percentage. Every move is
 * appended to the flight's history so a stalled plane, and any drift between what the position
 * claims and what the notes say, stay legible.
 */
// Only the plane's captain may move it (Matt, 2026-10-27); the server checks memberId holds the captain seat.
export function setFlightHill(flightId, position, note, memberId) {
  return post({ action: 'setHill', flightId, position, note, memberId: memberId || null })
}

/** One flight's movement record, newest first. */
export function getFlightHillHistory(flightId) {
  return post({ action: 'hillHistory', flightId })
}

/**
 * Everything needed to replay the hill on any past date: every open (and recently landed)
 * flight plus its full move log.
 *
 * There are deliberately no daily snapshots. The move log is append-only, so the state on any
 * date is the last move at or before it — one row per real move rather than a row per flight
 * per day, and it carries the note, which a snapshot could never reconstruct.
 */
export function getHillTimeline(days) {
  return post({ action: 'hillTimeline', days })
}

/** Land or cancel a flight. `reason`: 'posted' | 'released' | 'cancelled'. */
export function landFlight(flightId, reason) {
  return postAndAnnounce({ action: 'land', flightId, reason })
}

/**
 * Squadrons (database/202_squadrons.sql) — bundle up to 5 open threads into one crewed unit.
 * A squadron IS a flight (`kind: 'squadron'`), so landing one is just `landFlight(id, 'released')`
 * — no separate disband call. `threadIds` needs at least 2 (one thread is just a thread) and at
 * most 5; all must be on the same project. `members`: same shape as createFlight's, optional.
 */
export function createSquadron(threadIds, hill, members) {
  return postAndAnnounce({ action: 'createSquadron', threadIds, hill, members })
}

/** Add one more open thread to an already-open squadron (still capped at 5 total). */
export function addThreadToSquadron(flightId, threadId) {
  return postAndAnnounce({ action: 'addThreadToSquadron', flightId, threadId })
}

/**
 * Drop one thread back out of a squadron to fly solo again — its own individual tracking
 * resumes automatically on the next board load. Refuses if it's the squadron's last thread;
 * disband the squadron instead (landFlight(flightId, 'released')) for that case.
 */
export function removeThreadFromSquadron(flightId, threadId) {
  return postAndAnnounce({ action: 'removeThreadFromSquadron', flightId, threadId })
}

/**
 * Crew a bare thread (database/204_crew_two_and_thread_crew.sql) — mints a `kind:'thread'`
 * flight so this occupies one of the person's two simultaneous-assignment slots, exactly like
 * crewing a milestone or a squadron does. Never rendered as its own row; use `getFlightPlan`'s
 * merged `flights` array (joined onto the thread by `threadId` in FlightPlanTab.jsx) to read its
 * crew back. Re-crewing an already-crewed thread: use `setFlightCrew(crewFlightId, members)`.
 */
export function crewThread(threadId, members, hill) {
  return postAndAnnounce({ action: 'crewThread', threadId, members, hill })
}

/**
 * Crew roster exceptions (database/210_crew_roster.sql — decision 8 in
 * docs/FLOW_RECONCILIATION_PLAN.md): who on the internal Member roster is NOT crew — a seat that
 * never flies (`eligible: false`) or someone who has left (`retiredAt`). Absence = eligible. The
 * crew picker filters the team through this; the tower edits it from the same picker.
 * Resolves to [{ memberAirtableId, memberName, eligible, retiredAt, note, updatedAt, updatedBy }].
 */
export function getCrewRoster() {
  return post({ action: 'crewRoster' }).then(d => d.roster || [])
}

/** Upsert one roster row. `retired` true keeps an existing retired date rather than restamping it.
 *  `timezone` (IANA name, or null for the studio's Pacific) is the person's own day for the captain's
 *  report; leave it undefined to keep whatever is stored. */
export function setCrewRoster({ memberAirtableId, memberName, eligible, retired, note, timezone }) {
  return post({ action: 'setCrewRoster', memberAirtableId, memberName, eligible, retired, note, timezone }).then(d => d.row)
}

/** Pair two people (or clear with partnerId null), and set a person's first call / solo work
 *  ('permit' | 'construction' | null). Leave a field undefined to keep it. Resolves to the whole roster
 *  (database/344, Flight Board → Crews). */
export function setCrewPair({ memberAirtableId, memberName, partnerId, partnerName, firstCall, soloOn }) {
  return post({ action: 'setCrewPair', memberAirtableId, memberName, partnerId, partnerName, firstCall, soloOn }).then(d => d.roster || [])
}

/** Apply the roster to a team list: drop anyone marked ineligible or retired. */
/** The team who can HOLD A ROLE on the Org Chart (Matt, 2026-10-21: "Why can't I add Gabriel as
 *  Bookkeeper?"). Not the crew list: not flying planes says nothing about holding a role. Drops only
 *  people who have left and the roster's placeholder seats (noted "not a person" / "placeholder"). */
export function roleHolderTeam(team, roster) {
  const block = new Set((roster || []).filter(r => r.retiredAt || (r.eligible === false && /not a person|placeholder/i.test(r.note || ''))).map(r => r.memberAirtableId))
  return (team || []).filter(t => !block.has(t.id))
}

export function eligibleCrew(team, roster) {
  const block = new Set((roster || []).filter(r => r.eligible === false || r.retiredAt).map(r => r.memberAirtableId))
  return (team || []).filter(t => !block.has(t.id))
}

/**
 * THE CAPTAIN'S DAILY REPORT (2026-09-11, docs/CAPTAIN_REPORT.md). Any signed-in user may call these
 * — captains are staff seats, not tower seats; the server checks the caller holds the captain seat.
 */
/** The morning crew card (2026-10-27): { today, acked, flights } for this person. */
export function getCrewFocus(memberId) {
  return post({ action: 'crewFocus', memberId })
}
export function ackCrewFocus({ memberId, memberName, flights }) {
  return post({ action: 'ackCrewFocus', memberId, memberName, flights })
}
export function getCaptainReportsDue(memberId) {
  return post({ action: 'captainReportsDue', memberId })
}
export function submitCaptainReport({ memberId, memberName, flightId, hillPosition, daysRemaining, blocker, note, waiting, requests, received, resend, withdraw, selfChase, keepWorking }) {
  return post({ action: 'submitCaptainReport', memberId, memberName, flightId, hillPosition, daysRemaining, blocker, note, waiting: !!waiting, requests, received, resend, withdraw, selfChase, keepWorking })
}

/**
 * CREW REQUESTS (2026-10-15, api/_flightRequests.js). The review-and-direction roles' asks (calling
 * the ball), a coordinator's requests to chase, and the answers to either. `tz` is the browser's own,
 * so "today" (a called ball not taken today drops) is the person's own day.
 */
function myTz() { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch { return '' } }
export function getReviewAsks(memberId) { return post({ action: 'reviewAsks', memberId, tz: myTz() }).then(d => d.asks || []) }
export function getCoordinatorRequests(memberId) { return post({ action: 'coordinatorRequests', memberId, tz: myTz() }).then(d => d.requests || []) }
/** action: mine | you (toMemberId,toName) | answer (text) | sendBack (text) | chased (expectedDate) | cameBack | escalate (text) | talked | notRequest (text) */
/** Member ids marked as gone in Admin -> Crew; never offered as someone to hand a ball to. */
/** The caller's own review time: free time today, next review/direction blocks, and their queue. */
export function getReviewWindow(memberId) { return post({ action: 'reviewWindow', memberId, tz: myTz() }) }
/** Flight hover card: what a flight number is (any signed-in user). */
export function lookupFlight(number) { return post({ action: 'flightLookup', number }).then(d => d.flight || null) }
export function getHandoffRetired() { return post({ action: 'handoffRetired' }).then(d => d.ids || []) }
// The card's own verb travels as `op`: a field called `action` would overwrite the route name.
export function requestAction(fields) { const { action: op, ...rest } = fields; return post({ ...rest, op, action: 'requestAction', tz: myTz() }) }

/**
 * ASKS FOR THE TOWER (2026-09-16): the open "need direction from Matt or Brandon" reports, and the two
 * answers. Tower seats only. `ackWaiting` records that the tower read a "nothing left but waiting" and
 * chose to leave the plane in the air; pulling or landing it are the ordinary `landFlight` paths.
 */
export function getDirectionAsks() { return post({ action: 'directionAsks' }).then(d => d.asks || []) }
export function ackDirectionAsk({ reportId, kind, date, memberName }) {
  return post({ action: 'ackDirectionAsk', reportId, kind, date, memberName })
}
export function ackWaiting(flightId) { return post({ action: 'ackWaiting', flightId }) }

/** Half-made triage answers, kept on the server per person (database/336): resumes after a reload or on another computer. */
export function getTriageDraft(memberId, kind, key, day) { return post({ action: 'getTriageDraft', memberId, kind, key, day }).then(r => (r && r.draft) || null) }
export function saveTriageDraft(memberId, kind, key, day, payload) { return post({ action: 'saveTriageDraft', memberId, kind, key, day, payload }) }
export function clearTriageDraft(memberId, kind, key) { return post({ action: 'clearTriageDraft', memberId, kind, key }) }
